import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "@tanstack/react-router";
import type { PulseWithPostData } from "../../types/pulse.types";
import {
  useGetPulseSnapshot,
  useRefreshPulseSnapshot,
  useUpdatePulse,
} from "../../api/pulse.queries";
import { ChangeIndicatorCompact } from "./ChangeIndicator";
import { CommentsIcon } from "@/assets/icons/comments";
import { FbLikeIcon } from "@/assets/icons/fb-like";
import { ShareIcon } from "@/assets/icons/share";
import { useDeletePulse } from "../../api/pulse.queries";
import { Spinner } from "@/components/ui/spinner";

interface PulseFieldsProps {
  pulse: PulseWithPostData;
  showPostDataStatus?: boolean;
}

export function PulseFields({
  pulse,
  showPostDataStatus = false,
}: PulseFieldsProps) {
  const navigate = useNavigate();
  // Don't auto-fetch snapshot - only fetch on manual refresh
  const { data: snapshot, isLoading: isSnapshotLoading } = useGetPulseSnapshot(
    pulse._id,
    false, // Disabled by default
  );
  const refreshSnapshot = useRefreshPulseSnapshot();
  const updatePulseMutation = useUpdatePulse();

  const getPostDataStatusIndicator = () => {
    if (!showPostDataStatus) return null;

    if (pulse.postDataLoading) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-blue-600">
          <Spinner size="sm" />
          Time out at {new Date(pulse.timeout_at).toLocaleString()}
        </span>
      );
    }

    if (pulse.postDataError) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-red-600">
          <i className="ri-error-warning-line"></i>
          Failed to load post data
        </span>
      );
    }

    if (pulse.postData) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-green-600">
          <i className="ri-check-line"></i>
          Time out at {new Date(pulse.timeout_at).toLocaleString()}
        </span>
      );
    }

    return null;
  };

  const handleRefresh = () => {
    refreshSnapshot.mutate({ pulseId: pulse._id, postUrl: pulse.post_url });
  };

  const handleUpdate = () => {
    updatePulseMutation.mutate(pulse._id);
  };

  const { mutate: deletePulseMutation, isPending: isDeletePulsePending } =
    useDeletePulse();

  const handleDelete = () => {
    deletePulseMutation(pulse._id);
  };

  const hasChanges = snapshot?.has_changes ?? false;

  const getEngagementChanges = () => {
    // WORKAROUND: API has labels backwards - previous_snapshot is actually the NEW data
    const actualCurrentSnapshot =
      snapshot?.previous_snapshot || snapshot?.current_snapshot;
    if (!actualCurrentSnapshot?.engagements) return null;

    const current = actualCurrentSnapshot.engagements;
    const likes = (current.soc_like_count || 0) + (current.soc_love_count || 0);
    const comments = current.soc_comment_count || 0;
    const shares = current.soc_share_count || 0;
    const views = current.soc_view_count || 0;

    // Get changes from snapshot.changes object
    const likesChange =
      snapshot.changes?.["engagements.soc_like_count"]?.change || 0;
    const commentsChange =
      snapshot.changes?.["engagements.soc_comment_count"]?.change || 0;
    const sharesChange =
      snapshot.changes?.["engagements.soc_share_count"]?.change || 0;
    const viewsChange =
      snapshot.changes?.["engagements.soc_view_count"]?.change || 0;

    const likesPercentage =
      snapshot.changes?.["engagements.soc_like_count"]?.change_percentage;
    const commentsPercentage =
      snapshot.changes?.["engagements.soc_comment_count"]?.change_percentage;
    const sharesPercentage =
      snapshot.changes?.["engagements.soc_share_count"]?.change_percentage;
    const viewsPercentage =
      snapshot.changes?.["engagements.soc_view_count"]?.change_percentage;

    return {
      likes,
      comments,
      shares,
      views,
      likesChange,
      commentsChange,
      sharesChange,
      viewsChange,
      likesPercentage,
      commentsPercentage,
      sharesPercentage,
      viewsPercentage,
    };
  };

  const engagements = useMemo(() => getEngagementChanges(), [snapshot]);

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto">
      <div className="flex-1 justify-between overflow-x-hidden overflow-y-auto">
        <div className="grid h-auto w-full grid-cols-[1fr_auto] items-start gap-2.5 border-b border-neutral-200 p-3 pb-6">
          <div
            className="cursor-pointer"
            onClick={() =>
              navigate({
                to: "/pulse/info",
                search: { pulseId: pulse._id },
              })
            }
          >
            <h1 className="text-lg leading-[36px] font-bold -tracking-wide text-neutral-950">
              {pulse.pulse_name}
            </h1>
            <p className="text-xs leading-[140%] font-bold text-neutral-600">
              Latest Update:{" "}
              {pulse.postData?.date_updated
                ? new Date(pulse.postData.date_updated).toLocaleString()
                : "N/A"}
            </p>
            {getPostDataStatusIndicator()}

            {/* Snapshot Overview */}
            {isSnapshotLoading ? (
              <div className="mt-3 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : snapshot?.has_changes ? (
              <div className="mt-3 w-fit rounded-lg border border-amber-200 bg-amber-50 p-2">
                <div className="flex items-center gap-1 text-xs font-semibold text-amber-700">
                  <i className="ri-notification-line"></i>
                  Changes Detected
                </div>
                <div className="space-y-1.5">
                  {engagements && engagements.likesChange !== 0 && (
                    <ChangeIndicatorCompact
                      label="Likes"
                      value={engagements.likes}
                      change={engagements.likesChange}
                      changePercentage={engagements.likesPercentage}
                      icon={<FbLikeIcon width={14} height={14} />}
                    />
                  )}
                  {engagements && engagements.commentsChange !== 0 && (
                    <ChangeIndicatorCompact
                      label="Comments"
                      value={engagements.comments}
                      change={engagements.commentsChange}
                      changePercentage={engagements.commentsPercentage}
                      icon={<CommentsIcon width={14} height={14} />}
                    />
                  )}
                  {engagements && engagements.sharesChange !== 0 && (
                    <ChangeIndicatorCompact
                      label="Shares"
                      value={engagements.shares}
                      change={engagements.sharesChange}
                      changePercentage={engagements.sharesPercentage}
                      icon={<ShareIcon width={14} height={14} />}
                    />
                  )}
                  {engagements && engagements.viewsChange !== 0 && (
                    <ChangeIndicatorCompact
                      label="Views"
                      value={engagements.views}
                      change={engagements.viewsChange}
                      changePercentage={engagements.viewsPercentage}
                      icon={<i className="ri-eye-line text-[14px]"></i>}
                    />
                  )}
                </div>
              </div>
            ) : snapshot ? (
              <div className="mt-3 text-xs text-neutral-500">
                <i className="ri-check-line"></i> No changes detected
              </div>
            ) : null}
          </div>
          <div className="flex w-full flex-row items-center gap-2">
            <Button
              variant="outline"
              className="h-[45px] w-fit"
              onClick={handleRefresh}
              disabled={refreshSnapshot.isPending}
              icon
            >
              {refreshSnapshot.isPending ? (
                <div className="flex h-full items-center justify-center text-white">
                  <Spinner size="sm" className="stroke-primary!" />
                </div>
              ) : (
                <i className="ri-refresh-line text-[16px] font-light"></i>
              )}
            </Button>
            <Button
              variant="outline"
              className="h-[45px] w-fit"
              onClick={handleUpdate}
              disabled={updatePulseMutation.isPending || !hasChanges}
              icon
            >
              Sync
            </Button>
            <Button
              variant="destructive"
              className="h-[45px] w-[45px] bg-red-100 hover:scale-none hover:bg-red-100/80"
              icon
              onClick={handleDelete}
              disabled={isDeletePulsePending}
            >
              {isDeletePulsePending ? (
                <div className="flex h-full items-center justify-center text-white">
                  <Spinner size="sm" />
                </div>
              ) : (
                <i className="ri-delete-bin-line text-[16px] font-light"></i>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
